import React, { useEffect, useRef } from 'react';
import { useThree, invalidate, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { editorStore } from '../../store/editorStore';
import { observer } from 'mobx-react-lite';
import { projectToWorld } from '../FloorPlan/projectToWorld';
import { floorplannerStore } from '../../store/floorplannerStore';
import { WallConnectionEnd, WallConnectionStart, WallType } from '../../types/floorplannerTypes';
import { updateAlignmentLines } from '../FloorPlan/updateAligmentLines';
import { renderStore } from '../../store/renderStore';
import { undoRedoManager } from '../../store/undoRedoManager';
import { wallUpdateManager } from '../../utils/wallUpdateManager';
import { wallGeometryCache } from '../../utils/WallGeometryCache';
import { applyAlignmentAfterSnap } from '../FloorPlan/applyAlignmentAfterSnap';
import { applyWallPivot } from '../FloorPlan/applyWallPivot';
import { calculateWallEdgePoint, determineConnectionSide, getClosestPosition, getWallAngle, createEdgeConnection } from '../../utils/edgeConnectionHelpers';
import { WALL_ADD_MIN_INITIAL_LENGTH } from '../../utils/wallHoverAddButton';
import { runInAction } from 'mobx';

const WallConstructionPlane = observer(() => {
  const {
    wallConstructionMode,
    wallConstructionModeAddNewWall,
    wallConstructionModeUndoLastWallRequest,
    wallConstructionSeedRequestId,
    view3D,
  } = editorStore;
  const { gl, camera } = useThree();
  const isActive = wallConstructionMode && !view3D;
  const addNewWall = useRef(false);
  const currentWall = useRef(undefined as string | undefined);
  const lastClickPosition = useRef(undefined as THREE.Vector2 | undefined);
  const firstClickPosition = useRef<THREE.Vector2 | undefined>(undefined);
  const previousWallId = useRef<string | undefined>(undefined);
  const firstWallId = useRef<string | undefined>(undefined);
  /** Seed from hover "+" when the new wall should attach mid-span / at start of an existing wall. */
  const seedAttach = useRef<{
    wallId: string;
    endpoint: "start" | "end" | "middle";
    projectedPoint: THREE.Vector2;
  } | null>(null);
  const lastAppliedSeedRequestId = useRef(0);
  const pendingMove = useRef<{ position: THREE.Vector2; shiftKey: boolean } | null>(null);
  const rafId = useRef<number | null>(null);
  const lastHandledUndoRequest = useRef(wallConstructionModeUndoLastWallRequest);

  const getActiveConstructionWallId = (): string | undefined => {
    if (currentWall.current && floorplannerStore.wallsMap.has(currentWall.current)) {
      return currentWall.current;
    }

    return undefined;
  };

  const getFinalizedConstructionWallIdForUndo = (): string | undefined => {
    if (previousWallId.current && floorplannerStore.wallsMap.has(previousWallId.current)) {
      return previousWallId.current;
    }

    return undefined;
  };

  const getPreviousWallIdForUndo = (wall: WallType): string | undefined => {
    const dragState = floorplannerStore.wallDragState.get(wall.id);
    const previousFromDragEdge = dragState?.originalEdgeConnections?.find(connection =>
      connection.sourceEndpoint === "start" &&
      connection.targetWallId !== wall.id &&
      floorplannerStore.wallsMap.has(connection.targetWallId)
    )?.targetWallId;

    if (previousFromDragEdge) {
      return previousFromDragEdge;
    }

    const previousFromDragCenter = dragState?.originalConnections?.find(connection =>
      connection.sourcePosition === WallConnectionStart &&
      connection.id !== wall.id &&
      floorplannerStore.wallsMap.has(connection.id)
    )?.id;

    if (previousFromDragCenter) {
      return previousFromDragCenter;
    }

    const previousFromEdge = wall.edgeConnections?.find(connection =>
      connection.sourceEndpoint === "start" &&
      connection.targetWallId !== wall.id &&
      floorplannerStore.wallsMap.has(connection.targetWallId)
    )?.targetWallId;

    if (previousFromEdge) {
      return previousFromEdge;
    }

    const previousFromCenter = wall.centerConnections?.find(connection =>
      connection.sourcePosition === WallConnectionStart &&
      connection.id !== wall.id &&
      floorplannerStore.wallsMap.has(connection.id)
    )?.id;

    if (previousFromCenter) {
      return previousFromCenter;
    }

    if (previousWallId.current && previousWallId.current !== wall.id && floorplannerStore.wallsMap.has(previousWallId.current)) {
      return previousWallId.current;
    }

    return undefined;
  };

  const removeDanglingWallReferences = (removedWallId: string) => {
    runInAction(() => {
      floorplannerStore.wallsMap.forEach((wall, wallId) => {
        let changed = false;

        if (wall.edgeConnections?.some(connection => connection.targetWallId === removedWallId || connection.sourceWallId === removedWallId)) {
          wall.edgeConnections = wall.edgeConnections.filter(connection =>
            connection.targetWallId !== removedWallId &&
            connection.sourceWallId !== removedWallId
          );
          changed = true;
        }

        if (wall.centerConnections?.some(connection => connection.id === removedWallId)) {
          wall.centerConnections = wall.centerConnections.filter(connection => connection.id !== removedWallId);
          changed = true;
        }

        if (!changed) {
          return;
        }

        delete wall.cornerState;
        floorplannerStore.objectNeedsUpdate(wallId);
        renderStore.clearWallShapes(wallId);
        renderStore.generateJointPolygons(wallId);
      });
    });
  };

  const removeConstructionWall = (wallId: string): WallType | undefined => {
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) {
      return undefined;
    }

    wallUpdateManager.enableProgressiveModeForWall(wallId, false);
    floorplannerStore.wallDragState.delete(wallId);
    floorplannerStore.unlockMeasurementDisplaySide(wallId);
    const undoStackLengthBeforeRemoval = undoRedoManager.undoStack.length;
    floorplannerStore.removeWall(wallId);
    undoRedoManager.pruneObjectChanges("wall", wallId, {
      undoStackEntriesBefore: undoStackLengthBeforeRemoval,
    });
    removeDanglingWallReferences(wallId);
    renderStore.clearWallShapes(wallId);
    return wall;
  };

  const resetPointerMoveState = () => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    pendingMove.current = null;
    renderStore.clearAlignmentLines();
  };

  const resetConstructionToPreviousWall = (previousWallIdForUndo: string | undefined) => {
    const previousWall = previousWallIdForUndo
      ? floorplannerStore.wallsMap.get(previousWallIdForUndo)
      : undefined;

    if (previousWall) {
      wallUpdateManager.enableProgressiveModeForWall(previousWall.id, false);
      floorplannerStore.wallDragState.delete(previousWall.id);
      floorplannerStore.unlockMeasurementDisplaySide(previousWall.id);
      previousWallId.current = previousWall.id;
      lastClickPosition.current = previousWall.end.clone();
      addNewWall.current = true;
      currentWall.current = undefined;
      editorStore.setWallEditingLengthPanel(floorplannerStore.wallLength(previousWall));
      floorplannerStore.objectNeedsUpdate(previousWall.id);
    } else {
      currentWall.current = undefined;
      previousWallId.current = undefined;
      lastClickPosition.current = undefined;
      firstClickPosition.current = undefined;
      firstWallId.current = undefined;
      addNewWall.current = false;
      editorStore.setWallEditingLengthPanel(undefined);
    }

    editorStore.clearSelections();
    renderStore.setIsDragging(true);
    document.body.style.cursor = 'crosshair';
    invalidate();
  };

  const undoLatestConstructionWall = (): boolean => {
    resetPointerMoveState();

    const activeWallId = getActiveConstructionWallId();
    const finalizedWallId = getFinalizedConstructionWallIdForUndo();

    if (!finalizedWallId) {
      if (!activeWallId) {
        return false;
      }

      const activeWall = floorplannerStore.wallsMap.get(activeWallId);
      if (!activeWall) {
        return false;
      }

      const startPosition = activeWall.start.clone();
      removeConstructionWall(activeWallId);
      currentWall.current = undefined;
      previousWallId.current = undefined;
      lastClickPosition.current = startPosition;
      firstClickPosition.current = startPosition;
      firstWallId.current = undefined;
      addNewWall.current = true;
      editorStore.clearSelections();
      editorStore.setWallEditingLengthPanel(undefined);
      renderStore.setIsDragging(true);
      document.body.style.cursor = 'crosshair';
      invalidate();
      return true;
    }

    const finalizedWall = floorplannerStore.wallsMap.get(finalizedWallId);
    if (!finalizedWall) {
      return false;
    }

    const previousWallIdForUndo = getPreviousWallIdForUndo(finalizedWall);

    if (activeWallId && activeWallId !== finalizedWallId) {
      removeConstructionWall(activeWallId);
    }

    removeConstructionWall(finalizedWallId);

    if (firstWallId.current === finalizedWallId) {
      firstWallId.current = previousWallIdForUndo;
      firstClickPosition.current = previousWallIdForUndo
        ? firstClickPosition.current
        : undefined;
    }

    resetConstructionToPreviousWall(previousWallIdForUndo);
    return true;
  };

  const getSnappedEndPosition = (start: THREE.Vector2, rawEnd: THREE.Vector2, enableAxisSnap: boolean): {
    position: THREE.Vector2;
    snappedAngle: number | undefined;
  } => {
    if (!enableAxisSnap) {
      return { position: rawEnd, snappedAngle: undefined };
    }

    const deltaX = rawEnd.x - start.x;
    const deltaY = rawEnd.y - start.y;
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    const distance = start.distanceTo(rawEnd);
    const normalizedAngle = (angle + 360) % 360;
    const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
    const closestSnapAngle = snapAngles.reduce(
      (prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev,
    );
    const radianSnapAngle = closestSnapAngle * (Math.PI / 180);
    const snapEndX = start.x + distance * Math.cos(radianSnapAngle);
    const snapEndY = start.y + distance * Math.sin(radianSnapAngle);
    const snappedPosition = new THREE.Vector2(snapEndX, snapEndY);

    if (rawEnd.distanceTo(snappedPosition) < editorStore.interactionSnappingTolerance()) {
      // Return the snapped angle (normalized to 0-359)
      const normalizedSnapAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
      return { position: snappedPosition, snappedAngle: normalizedSnapAngle };
    }

    return { position: rawEnd, snappedAngle: undefined };
  };

  const finalizeActiveWall = (wall: WallType) => {
    // CRITICAL: Use the wall's CURRENT end position, not the click position.
    // The wall already has the correct snapped position from the drag phase (processPointerMove).
    // Using the click position would cause the wall to jump if the cursor moved slightly
    // between the last pointer move and the click, or if alignment snapping was active.
    let end = wall.end.clone();
    let start = wall.start.clone();

    // Verify/recalculate the start position for corner-to-corner alignment.
    // Since we're using the wall's current end position from the drag phase,
    // this should converge immediately as the geometry is already correct.
    // This acts as a safety check to ensure proper corner anchoring.
    if (previousWallId.current && !editorStore.isAltPressed) {
      const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
      if (prevWall) {
        const wallWidth = wall.wallWidth || floorplannerStore.wallWidth;
        const halfWidth = wallWidth / 2;
        const prevAngle = getWallAngle(prevWall);
        const prevDir = new THREE.Vector2(Math.cos(prevAngle), Math.sin(prevAngle));
        const prevPerp = new THREE.Vector2(-prevDir.y, prevDir.x);

        // Calculate the FIXED corners of the previous wall's end
        const prevHalfWidth = (prevWall.wallWidth || floorplannerStore.wallWidth) / 2;
        const prevCornerLeft = prevWall.end.clone().add(prevPerp.clone().multiplyScalar(prevHalfWidth));
        const prevCornerRight = prevWall.end.clone().sub(prevPerp.clone().multiplyScalar(prevHalfWidth));

        // Iterate to find the correct start position (same logic as createNewWallSegment)
        const maxIterations = 5;
        const convergenceEpsilon = 1e-6;

        for (let i = 0; i < maxIterations; i++) {
          const currentAngle = Math.atan2(end.y - start.y, end.x - start.x);
          const side = determineConnectionSide(prevDir, currentAngle);
          const prevCorner = side === "left" ? prevCornerLeft : prevCornerRight;

          const newDir = new THREE.Vector2(Math.cos(currentAngle), Math.sin(currentAngle));
          const newPerp = new THREE.Vector2(-newDir.y, newDir.x);
          const sideSign = side === "left" ? 1 : -1;
          const offset = newPerp.clone().multiplyScalar(sideSign * halfWidth);

          const newStart = prevCorner.clone().sub(offset);
          const delta = newStart.distanceTo(start);
          start = newStart;

          if (delta < convergenceEpsilon) {
            break;
          }
        }
      }
    }

    // Check if we're closing the loop by looking at the first wall's actual start position
    // Use the wall's CURRENT end position (which may be positioned at corner from drag)
    // rather than the re-calculated end position
    const currentWallEnd = wall.end;

    // We need to check both corners of the first wall's start, not just the center,
    // since walls connect edge-to-edge at corners
    let closingToStart = false;
    let startWall: WallType | undefined = undefined;

    if (firstWallId.current && firstWallId.current !== wall.id) {
      startWall = floorplannerStore.wallsMap.get(firstWallId.current);
      if (startWall) {
        // Calculate the two corner positions of the first wall's start
        const startWallDir = new THREE.Vector2().subVectors(startWall.end, startWall.start);
        const startWallLen = startWallDir.length();
        if (startWallLen > 1e-6) {
          const startWallDirNorm = startWallDir.clone().normalize();
          const startWallPerp = new THREE.Vector2(-startWallDirNorm.y, startWallDirNorm.x);
          const halfWidth = (startWall.wallWidth || floorplannerStore.wallWidth) / 2;

          // Calculate both corners of the first wall's start
          const cornerLeft = startWall.start.clone().add(startWallPerp.clone().multiplyScalar(halfWidth));
          const cornerRight = startWall.start.clone().sub(startWallPerp.clone().multiplyScalar(halfWidth));

          // Check if we're close to the center or either corner
          // Use wall half-width as the primary tolerance - this ensures the detection works
          // regardless of snapping tolerance setting
          // Check BOTH the current wall end (from drag) AND the calculated end (from click)
          const cornerTolerance = Math.max(halfWidth, editorStore.interactionSnappingTolerance());

          // Check the wall's current end position (positioned at corner from drag)
          const currentCenterDist = currentWallEnd.distanceTo(startWall.start);
          const currentLeftDist = currentWallEnd.distanceTo(cornerLeft);
          const currentRightDist = currentWallEnd.distanceTo(cornerRight);

          // Also check the snapped click position
          const clickCenterDist = end.distanceTo(startWall.start);
          const clickLeftDist = end.distanceTo(cornerLeft);
          const clickRightDist = end.distanceTo(cornerRight);

          closingToStart = currentCenterDist < cornerTolerance ||
            currentLeftDist < cornerTolerance ||
            currentRightDist < cornerTolerance ||
            clickCenterDist < cornerTolerance ||
            clickLeftDist < cornerTolerance ||
            clickRightDist < cornerTolerance;
        } else {
          // Fallback to center check if wall length is too small
        const snapTolerance = editorStore.interactionSnappingTolerance();
        closingToStart = currentWallEnd.distanceTo(startWall.start) < snapTolerance ||
          end.distanceTo(startWall.start) < snapTolerance;
        }
      }
    }

    // Fallback to firstClickPosition if firstWallId is not set
    if (!closingToStart && !startWall && firstClickPosition.current) {
      const snapTolerance = editorStore.interactionSnappingTolerance();
      closingToStart = end.distanceTo(firstClickPosition.current) < snapTolerance;
      if (closingToStart) {
        startWall = Array.from(floorplannerStore.wallsMap.values()).find(w =>
          w.start.distanceTo(end) < snapTolerance && w.id !== wall.id
        );
        // If we can't find the start wall, don't treat this as closing the loop
        if (!startWall) {
          closingToStart = false;
        }
      }
    }

    if (closingToStart && startWall) {
      // FORCE corner-to-corner connection when closing the loop.
      // Always calculate the correct end position to ensure corner-to-corner,
      // regardless of where the user placed the last wall segment.
      if (!editorStore.isAltPressed) {
        const startWallDir = new THREE.Vector2().subVectors(startWall.end, startWall.start);
        const startWallLen = startWallDir.length();
        const startWallHalfWidth = (startWall.wallWidth || floorplannerStore.wallWidth) / 2;
        const lastWallHalfWidth = (wall.wallWidth || floorplannerStore.wallWidth) / 2;

        if (startWallLen > 1e-6) {
          const startWallDirNorm = startWallDir.clone().normalize();
          const startWallPerp = new THREE.Vector2(-startWallDirNorm.y, startWallDirNorm.x);

          // Calculate both corners of the first wall's start
          const cornerLeft = startWall.start.clone().add(startWallPerp.clone().multiplyScalar(startWallHalfWidth));
          const cornerRight = startWall.start.clone().sub(startWallPerp.clone().multiplyScalar(startWallHalfWidth));

          // Use the actual wall start position (not the recalculated 'start' which may have fp errors)
          // to determine which corner is nearest
          const wallStart = wall.start.clone();

          // Calculate the last wall's corners at its start
          const lastWallDir = new THREE.Vector2().subVectors(end, wallStart);
          const lastWallLen = lastWallDir.length();

          if (lastWallLen > 1e-6) {
            const lastWallDirNorm = lastWallDir.clone().normalize();
            const lastWallPerp = new THREE.Vector2(-lastWallDirNorm.y, lastWallDirNorm.x);

            // Calculate corners of last wall at its start
            const lastWallStartCornerLeft = wallStart.clone().add(lastWallPerp.clone().multiplyScalar(lastWallHalfWidth));
            const lastWallStartCornerRight = wallStart.clone().sub(lastWallPerp.clone().multiplyScalar(lastWallHalfWidth));

            // Determine which corner pair should connect:
            // The last wall's corner at its end should touch the first wall's corner at its start
            // We need to pick corners that are on the "inside" of the loop

            // Check which first wall corner is closer to the last wall's start corners
            const distLeftToLeft = lastWallStartCornerLeft.distanceTo(cornerLeft);
            const distLeftToRight = lastWallStartCornerLeft.distanceTo(cornerRight);
            const distRightToLeft = lastWallStartCornerRight.distanceTo(cornerLeft);
            const distRightToRight = lastWallStartCornerRight.distanceTo(cornerRight);

            // Find the minimum distance - this tells us which corners should connect
            const minDist = Math.min(distLeftToLeft, distLeftToRight, distRightToLeft, distRightToRight);

            let targetCorner: THREE.Vector2;
            let connectionSide: "left" | "right";

            if (minDist === distLeftToLeft || minDist === distRightToLeft) {
              // Connect to left corner of first wall
              targetCorner = cornerLeft;
              connectionSide = "left";
            } else {
              // Connect to right corner of first wall
              targetCorner = cornerRight;
              connectionSide = "right";
            }

            // Now calculate the exact end position for the last wall
            // The last wall's corner at 'end' should touch targetCorner
            // corner at end = end + (sideSign * lastWallHalfWidth * perp)
            // We want: corner at end = targetCorner
            // So: end = targetCorner - (sideSign * lastWallHalfWidth * perp)

            // But we need the perpendicular based on the FINAL wall direction (from wallStart to end)
            // This requires iteration since end depends on the perpendicular which depends on end

            // Start with an initial estimate
            let estimatedEnd = targetCorner.clone();
            const maxIterations = 10;
            const convergenceEpsilon = 1e-9;

            for (let i = 0; i < maxIterations; i++) {
              const currentDir = new THREE.Vector2().subVectors(estimatedEnd, wallStart);
              const currentLen = currentDir.length();

              if (currentLen < 1e-6) break;

              const currentDirNorm = currentDir.clone().normalize();
              const currentPerp = new THREE.Vector2(-currentDirNorm.y, currentDirNorm.x);
              const sideSign = connectionSide === "left" ? 1 : -1;
              const cornerOffset = currentPerp.clone().multiplyScalar(sideSign * lastWallHalfWidth);

              // Calculate where end should be so that end + cornerOffset = targetCorner
              const newEnd = targetCorner.clone().sub(cornerOffset);

              const delta = newEnd.distanceTo(estimatedEnd);
              estimatedEnd = newEnd;

              if (delta < convergenceEpsilon) {
                break;
              }
            }

            end = estimatedEnd;

            // Also recalculate start to be consistent with the new angle if we have a previous wall connection
            if (previousWallId.current) {
              const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
              if (prevWall) {
                const prevAngle = getWallAngle(prevWall);
                const prevDir = new THREE.Vector2(Math.cos(prevAngle), Math.sin(prevAngle));
                const prevPerp = new THREE.Vector2(-prevDir.y, prevDir.x);
                const prevHalfWidth = (prevWall.wallWidth || floorplannerStore.wallWidth) / 2;
                const prevCornerLeft = prevWall.end.clone().add(prevPerp.clone().multiplyScalar(prevHalfWidth));
                const prevCornerRight = prevWall.end.clone().sub(prevPerp.clone().multiplyScalar(prevHalfWidth));

                const finalAngle = Math.atan2(end.y - wallStart.y, end.x - wallStart.x);
                const side = determineConnectionSide(prevDir, finalAngle);
                const prevCorner = side === "left" ? prevCornerLeft : prevCornerRight;

                const finalDir = new THREE.Vector2(Math.cos(finalAngle), Math.sin(finalAngle));
                const finalPerp = new THREE.Vector2(-finalDir.y, finalDir.x);
                const startSideSign = side === "left" ? 1 : -1;
                const startOffset = finalPerp.clone().multiplyScalar(startSideSign * lastWallHalfWidth);

                start = prevCorner.clone().sub(startOffset);
              }
            }
          }
        }
      }
    }

    floorplannerStore.updateObjectPosition(wall.id, start.clone(), end.clone());

    if (closingToStart && startWall) {
      if (!editorStore.isAltPressed) {
        const wallDir = new THREE.Vector2().subVectors(end, start);
        const wallAngle = Math.atan2(wallDir.y, wallDir.x);
        const startWallAngle = getWallAngle(startWall);
        const startWallDir = new THREE.Vector2(Math.cos(startWallAngle), Math.sin(startWallAngle));

        // determineConnectionSide expects: target wall direction, source wall angle
        const side = determineConnectionSide(startWallDir, wallAngle);

        const conn1 = createEdgeConnection(startWall, "start", wall, "end", undefined, side);
        floorplannerStore.addEdgeConnection(startWall.id, wall.id, "start", "end", conn1);

        const conn2 = createEdgeConnection(wall, "end", startWall, "start");
        floorplannerStore.addEdgeConnection(wall.id, startWall.id, "end", "start", conn2);

        floorplannerStore.objectNeedsUpdate(startWall.id);
        floorplannerStore.objectNeedsUpdate(wall.id);
      } else {
        // ALT is pressed - use center-based connection for loop closing
        floorplannerStore.snapToOtherObjects(wall.id, WallConnectionEnd, end.clone(), true);
        floorplannerStore.snapToOtherObjects(startWall.id, WallConnectionStart, startWall.start.clone(), true);
        floorplannerStore.objectNeedsUpdate(startWall.id);
        floorplannerStore.objectNeedsUpdate(wall.id);
      }
    } else {
      // Check for wall connections at the CURRENT position without moving the wall.
      // The wall's position is already correct from the drag phase - we're just establishing connections.
      // We do NOT update the wall position here, even if a nearby connection is found.
      if (editorStore.isAltPressed) {
        floorplannerStore.snapToOtherObjects(wall.id, WallConnectionEnd, end.clone(), true);
      } else {
        floorplannerStore.snapToOtherObjectsEdgeBased(
          wall.id,
          WallConnectionEnd,
          end.clone(),
          true,
          { allowDuringDeferred: false },
        );
      }
      // NOTE: We intentionally do NOT update the wall position based on snap results.
      // The wall should stay exactly where it was during the drag phase.
    }

    const updatedWall = floorplannerStore.wallsMap.get(wall.id);
    if (updatedWall) {
      const outerLength = floorplannerStore.wallLength(updatedWall);
      editorStore.setWallEditingLengthPanel(outerLength);
    }

    // Restore edge connections from drag state before finalizing
    // This reconnects the wall to the previous wall at the correct corner positions
    const dragState = floorplannerStore.wallDragState.get(wall.id);
    if (dragState?.originalEdgeConnections && dragState.originalEdgeConnections.length > 0) {
      const latestWall = floorplannerStore.wallsMap.get(wall.id);
      if (latestWall) {
        for (const originalEdgeConnection of dragState.originalEdgeConnections) {
          const targetWall = floorplannerStore.wallsMap.get(originalEdgeConnection.targetWallId);
          if (targetWall) {
            // Create fresh edge connection with updated positions
            const newAngle = getWallAngle(latestWall);
            const targetAngle = getWallAngle(targetWall);
            const targetDir = new THREE.Vector2(Math.cos(targetAngle), Math.sin(targetAngle));
            const side = determineConnectionSide(targetDir, newAngle);

            // Re-create connections. Mid-point attachments are one-way (no reciprocal).
            const projectedPoint =
              originalEdgeConnection.targetEndpoint === "middle"
                ? getClosestPosition(latestWall.start, targetWall).projectedPoint
                : undefined;
            const conn1 = createEdgeConnection(
              latestWall,
              originalEdgeConnection.sourceEndpoint,
              targetWall,
              originalEdgeConnection.targetEndpoint,
              projectedPoint,
              side,
            );
            floorplannerStore.addEdgeConnection(
              latestWall.id,
              targetWall.id,
              originalEdgeConnection.sourceEndpoint,
              originalEdgeConnection.targetEndpoint,
              conn1,
            );

            if (
              originalEdgeConnection.targetEndpoint === "start" ||
              originalEdgeConnection.targetEndpoint === "end"
            ) {
              const conn2 = createEdgeConnection(
                targetWall,
                originalEdgeConnection.targetEndpoint,
                latestWall,
                originalEdgeConnection.sourceEndpoint,
              );
              floorplannerStore.addEdgeConnection(
                targetWall.id,
                latestWall.id,
                originalEdgeConnection.targetEndpoint,
                originalEdgeConnection.sourceEndpoint,
                conn2,
              );
            }

            floorplannerStore.objectNeedsUpdate(targetWall.id);
          }
        }
      }
    }

    floorplannerStore.markObjectForRoomUpdate(wall.id);
    floorplannerStore.objectNeedsUpdate(wall.id);
    floorplannerStore.unlockMeasurementDisplaySide(wall.id);

    floorplannerStore.objectNeedsUpdate(wall.id);
    floorplannerStore.unlockMeasurementDisplaySide(wall.id);
    floorplannerStore.wallDragState.delete(wall.id);

    return { endPoint: end, closedLoop: !!closingToStart };
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (editorStore.wallConstructionMode) {
      event.stopPropagation();
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = new THREE.Vector2(pos[0], pos[1]);
      if (!firstClickPosition.current) {
        firstClickPosition.current = clickPosition.clone();
      }

      if (!currentWall.current) {
        lastClickPosition.current = clickPosition;
        addNewWall.current = true;
        return;
      }

      const wall = floorplannerStore.wallsMap.get(currentWall.current);
      if (!wall) {
        return;
      }

      const result = finalizeActiveWall(wall);
      if (!result) {
        return;
      }

      // Disable progressive mode for the old previous wall that was connected to this wall
      // It was put in progressive mode when this wall segment was created
      if (previousWallId.current && previousWallId.current !== wall.id) {
        wallUpdateManager.enableProgressiveModeForWall(previousWallId.current, false);
        floorplannerStore.wallDragState.delete(previousWallId.current);
      }

      lastClickPosition.current = result.endPoint.clone();
      previousWallId.current = wall.id;
      wallUpdateManager.enableProgressiveModeForWall(wall.id, false);

      if (result.closedLoop) {
        addNewWall.current = true;
        editorStore.setWallConstructionMode(false);
        floorplannerStore.markObjectForRoomUpdate(wall.id);
        floorplannerStore.wallDragState.delete(wall.id);
        return;
      }

      addNewWall.current = true;
      currentWall.current = undefined;
    }
  };

  const processPointerMove = () => {
    rafId.current = null;
    const move = pendingMove.current;
    pendingMove.current = null;
    if (!move || !editorStore.wallConstructionMode || editorStore.view3D) {
      return;
    }

    const pointerPosition = move.position;
    const distance = lastClickPosition.current?.distanceTo(pointerPosition) ?? 0;

    if (addNewWall.current && distance > 0.1 && lastClickPosition.current) {
      createNewWallSegment([pointerPosition.x, pointerPosition.y]);
    }

    // Update the wall's position if it's already been created
    if (!addNewWall.current && currentWall.current) {
      const wall = floorplannerStore.wallsMap.get(currentWall.current);
      if (!wall) {
        return;
      }

      // Check if we need to recalculate the connection side based on current wall direction
      // This handles the case where the user initially moved in one direction but then changed
      if (previousWallId.current && !editorStore.isAltPressed) {
        const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
        if (prevWall) {
          const dragState = floorplannerStore.wallDragState.get(wall.id);
          const currentEdgeConnections = dragState?.originalEdgeConnections;

          if (currentEdgeConnections && currentEdgeConnections.length > 0) {
            // Calculate current wall direction
            const currentDir = new THREE.Vector2().subVectors(pointerPosition, wall.start);
            if (currentDir.length() > 0.05) { // Only recalculate if wall has meaningful length
              const currentAngle = Math.atan2(currentDir.y, currentDir.x);
              const prevAngle = getWallAngle(prevWall);
              const prevDir = new THREE.Vector2(Math.cos(prevAngle), Math.sin(prevAngle));

              // Calculate what the correct connection side should be
              const correctSide = determineConnectionSide(prevDir, currentAngle);

              // Get the stored connection side from the edge connection
              const storedConnection = currentEdgeConnections[0];
              const storedSide = storedConnection ?
                (storedConnection.sourceEdgePoint.y > wall.start.y ||
                  storedConnection.sourceEdgePoint.x > wall.start.x ? "left" : "right") : undefined;

              // Check if the side needs to change by comparing perpendicular offsets
              const wallWidth = wall.wallWidth || floorplannerStore.wallWidth;
              const halfWidth = wallWidth / 2;
              const prevCornerCorrect = calculateWallEdgePoint(prevWall, "end", currentAngle, undefined, undefined, correctSide).edgePosition;

              // Calculate what the current wall's start SHOULD be for the correct side
              const newDir = new THREE.Vector2(Math.cos(currentAngle), Math.sin(currentAngle));
              const newPerp = new THREE.Vector2(-newDir.y, newDir.x);
              const sideSign = correctSide === "left" ? 1 : -1;
              const offset = newPerp.clone().multiplyScalar(sideSign * halfWidth);
              const correctStart = prevCornerCorrect.clone().sub(offset);

              // Check if the current start is significantly different from the correct start
              const startDiff = wall.start.distanceTo(correctStart);
              if (startDiff > halfWidth * 0.5) {
                // The connection side needs to change - update the wall's start position
                // Also update the edge connection in the drag state
                const newConn = createEdgeConnection(wall, "start", prevWall, "end", undefined, correctSide);

                // Calculate the FIXED pivot anchors from the previous wall's geometry
                const targetAngle = prevAngle;
                const prevPerp = new THREE.Vector2(-prevDir.y, prevDir.x);
                const prevHalfWidth = (prevWall.wallWidth || floorplannerStore.wallWidth) / 2;
                const prevCornerLeft = prevWall.end.clone().add(prevPerp.clone().multiplyScalar(prevHalfWidth));
                const prevCornerRight = prevWall.end.clone().sub(prevPerp.clone().multiplyScalar(prevHalfWidth));

                // The primary anchor is the corner we're now connecting to
                const primaryAnchor = correctSide === "left" ? prevCornerLeft : prevCornerRight;
                const alternateAnchor = correctSide === "left" ? prevCornerRight : prevCornerLeft;
                const primaryIsLeft = correctSide === "left";
                const initialSideSign: 1 | -1 = correctSide === "left" ? 1 : -1;

                const newPivotAnchors: Record<"start" | "end", any> = {
                  start: {
                    primaryAnchor,
                    alternateAnchor,
                    currentAnchor: "primary",
                    initialSideSign,
                    currentSideSign: initialSideSign,
                    targetAngle,
                    lastRelativeSign: initialSideSign,
                    primaryIsLeft,
                  }
                } as any;

                // Update drag state with new connection and EXPLICIT pivot anchors
                floorplannerStore.wallDragState.set(wall.id, {
                  ...dragState,
                  originalEdgeConnections: [newConn],
                  fixedEndpoints: {
                    start: correctStart.clone(),
                    end: pointerPosition.clone(),
                  },
                  pivotAnchors: newPivotAnchors, // Explicit pivot anchors, not undefined
                });

                // Update the wall's start position
                floorplannerStore.updateObjectPosition(wall.id, correctStart, pointerPosition, { bypassEdgeConstraints: true });

                // Force geometry updates
                floorplannerStore.setObjectProperty(wall.id, "cornerState", undefined);
                floorplannerStore.setObjectProperty(prevWall.id, "cornerState", undefined);
                renderStore.composeWallShape(wall.id, undefined);
                renderStore.composeWallShape(prevWall.id, undefined);

                const updatedWall = floorplannerStore.wallsMap.get(wall.id);
                if (updatedWall) {
                  const outerLength = floorplannerStore.wallLength(updatedWall);
                  editorStore.setWallEditingLengthPanel(outerLength);
                }
                return; // Exit early since we've already updated everything
              }
            }
          }
        }
      }

      const start = wall.start;

      // SNAPPING PRIORITY ORDER during wall construction:
      // 1. Wall edge corners (for wall-to-wall connections) - HIGHEST PRIORITY
      // 2. 45-degree angle snapping (when within 20 degrees of snap angle)
      // 3. Alignment lines - LOWEST PRIORITY

      let end = pointerPosition.clone();
      let snappedWallAngle: number | undefined = undefined;
      let edgeSnapApplied = false;

      // PRIORITY 1: Wall edge corner snapping (highest priority)
      // Check for edge snap BEFORE angle snapping - wall connections take priority
      if (!move.shiftKey && !editorStore.isAltPressed) {
        const edgeSnapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(wall.id, "end", pointerPosition);
        if (edgeSnapCandidate?.snappingPosition &&
            pointerPosition.distanceTo(edgeSnapCandidate.snappingPosition) < editorStore.interactionSnappingTolerance()) {
          end = edgeSnapCandidate.snappingPosition.clone();
          edgeSnapApplied = true;
          floorplannerStore.storeEdgeSnappingPreview(wall.id, "end", edgeSnapCandidate);
        } else {
          floorplannerStore.storeEdgeSnappingPreview(wall.id, "end", null);
        }
      } else if (editorStore.isAltPressed) {
        // Center mode: check for center-based snapping
        const centerSnapPoint = floorplannerStore.getSnappingPositionForDrag(wall.id, WallConnectionEnd, pointerPosition);
        if (centerSnapPoint && pointerPosition.distanceTo(centerSnapPoint) < editorStore.interactionSnappingTolerance()) {
          end = centerSnapPoint.clone();
          edgeSnapApplied = true; // Mark to skip angle snapping
        }
        floorplannerStore.storeEdgeSnappingPreview(wall.id, "end", null);
      }

      // PRIORITY 2: 45-degree angle snapping (only if no edge snap was applied)
      if (!edgeSnapApplied && !move.shiftKey) {
        const snapResult = getSnappedEndPosition(start, pointerPosition, true);
        end = snapResult.position;
        snappedWallAngle = snapResult.snappedAngle;
      }

      let finalPositions = { start: start.clone(), end: end.clone() };
      if (wall.type === "wall" && !editorStore.isAltPressed) {
        finalPositions = applyWallPivot(wall, finalPositions, "end");
      }

      // PRIORITY 3: Alignment snapping (lowest priority)
      // Skip if edge snap was applied (connection priority)
      // Pass the snapped wall angle so that alignment axes within 20° of the wall angle are filtered out
      const skipAlignment = edgeSnapApplied;
      const alignmentResult = skipAlignment ? { applied: false, positions: null } : applyAlignmentAfterSnap({
        objectId: wall.id,
        intendedPoint: finalPositions.end,
        connectionEnd: WallConnectionEnd,
        alignmentKey: "end",
        suppressSnapping: false,
        existingPositions: finalPositions,
        snappedWallAngle,
      });

      if (alignmentResult.applied && alignmentResult.positions) {
        finalPositions = alignmentResult.positions;
      }

      floorplannerStore.updateObjectPosition(wall.id, finalPositions.start, finalPositions.end, { bypassEdgeConstraints: true });
      updateAlignmentLines(wall.id, finalPositions.end.x, finalPositions.end.y, "end");

      // Clear corner state to prevent stale geometry from being applied during composition
      // This forces the wall to be redrawn as a full rectangle before fresh trimming
      floorplannerStore.setObjectProperty(wall.id, "cornerState", undefined);

      // Force immediate shape update for the active wall to prevent lag
      renderStore.composeWallShape(wall.id, undefined);

      // Ensure the wall being constructed is allowed to update visually and trigger invalidation
      renderStore.allowVisualUpdatesForWall(wall.id);
      renderStore.requestInvalidation(wall.id);

      // Also force update for the connected previous wall to ensure the joint/miter updates in real-time
      if (previousWallId.current) {
        const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
        if (prevWall) {
          floorplannerStore.setObjectProperty(prevWall.id, "cornerState", undefined);
          renderStore.composeWallShape(prevWall.id, undefined);
          renderStore.allowVisualUpdatesForWall(prevWall.id);
        }
      }

      const updatedWall = floorplannerStore.wallsMap.get(wall.id);
      if (updatedWall) {
        const outerLength = floorplannerStore.wallLength(updatedWall);
        editorStore.setWallEditingLengthPanel(outerLength);
      }
    }
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (!editorStore.wallConstructionMode || editorStore.view3D) {
      return;
    }

    event.stopPropagation();
    document.body.classList.remove("cursor-vector");
    document.body.style.cursor = 'crosshair';
    const { clientX, clientY } = event;
    const pos = projectToWorld(clientX, clientY, gl, camera);
    pendingMove.current = {
      position: new THREE.Vector2(pos[0], pos[1]),
      shiftKey: event.shiftKey,
    };

    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(processPointerMove);
    }
  };

  const handlePointerUp = () => {
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    if (editorStore.wallConstructionMode) {
      // Get the click position in world coordinates
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = new THREE.Vector2(pos[0], pos[1]);

      // Check if there's a current wall being constructed
      if (currentWall.current) {
        const wall = floorplannerStore.wallsMap.get(currentWall.current);
        if (wall) {
          const end = wall.end;

          // Calculate the distance between the click and the wall's endpoint
          const distance = clickPosition.distanceTo(end);

          // If the click is close enough to the endpoint, exit construction mode
          if (distance < editorStore.interactionSnappingTolerance()) {
            // Exit wall construction mode
            editorStore.setWallConstructionMode(false);
            // Perform any additional cleanup if necessary
            floorplannerStore.markObjectForRoomUpdate(wall.id);
            // Optionally, clear selections or update alignment lines
            editorStore.clearSelections();
            renderStore.clearAlignmentLines();
          }
        }
      }
    }
  };

  const createNewWallSegment = (pos: [number, number]) => {
    if (lastClickPosition.current && currentWall) {
      let start = lastClickPosition.current.clone();
      const end = new THREE.Vector2(pos[0], pos[1]);
      const wallWidth = floorplannerStore.wallWidth;

      let connectionSide: "left" | "right" | undefined;

      // Calculate adjusted start for edge connection if needed
      // The issue is a circular dependency: the start position depends on the wall direction,
      // but the wall direction depends on the start position. We solve this with iteration.
      if (previousWallId.current && !editorStore.isAltPressed) {
        const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
        if (prevWall) {
          const halfWidth = wallWidth / 2;
          const prevAngle = getWallAngle(prevWall);
          const prevDir = new THREE.Vector2(Math.cos(prevAngle), Math.sin(prevAngle));
          const prevPerp = new THREE.Vector2(-prevDir.y, prevDir.x);

          // Calculate the FIXED corners of the previous wall's end
          const prevHalfWidth = (prevWall.wallWidth || floorplannerStore.wallWidth) / 2;
          const prevCornerLeft = prevWall.end.clone().add(prevPerp.clone().multiplyScalar(prevHalfWidth));
          const prevCornerRight = prevWall.end.clone().sub(prevPerp.clone().multiplyScalar(prevHalfWidth));

          // Iterate to find the correct start position
          const maxIterations = 5;
          const convergenceEpsilon = 1e-6;

          for (let i = 0; i < maxIterations; i++) {
            // Calculate angle from current start to end
            const currentAngle = Math.atan2(end.y - start.y, end.x - start.x);

            // Determine which side to connect based on current angle
            const side = determineConnectionSide(prevDir, currentAngle);
            connectionSide = side; // Always update to the final side

            // Select the appropriate FIXED corner based on the side
            const prevCorner = side === "left" ? prevCornerLeft : prevCornerRight;

            // Calculate offset based on the CURRENT angle (not the initial estimate)
            const newDir = new THREE.Vector2(Math.cos(currentAngle), Math.sin(currentAngle));
            const newPerp = new THREE.Vector2(-newDir.y, newDir.x);
            const sideSign = side === "left" ? 1 : -1;
            const offset = newPerp.clone().multiplyScalar(sideSign * halfWidth);

            // Calculate new start: the wall's corner at start should touch prevCorner
            const newStart = prevCorner.clone().sub(offset);

            // Check for convergence
            const delta = newStart.distanceTo(start);
            start = newStart;

            if (delta < convergenceEpsilon) {
              break;
            }
          }
        }
      }

      // Create a new wall object
      const newWall: WallType = {
        id: floorplannerStore.generateId(),
        type: "wall",
        start: start as THREE.Vector2,
        end: end as THREE.Vector2,
        wallWidth: wallWidth,
      };
      floorplannerStore.addWall(newWall);
      floorplannerStore.lockMeasurementDisplaySide(newWall.id);

      // Track the first wall for loop closing detection
      if (!firstWallId.current) {
        firstWallId.current = newWall.id;
      }

      //editorStore.setWallEditingOuterLength(newWall.id);  // This will make the wall width editable while constructing
      let initialEdgeConnection: any = undefined;
      const pendingSeedAttach = seedAttach.current;
      seedAttach.current = null;

      if (previousWallId.current) {
        if (editorStore.isAltPressed) {
          floorplannerStore.snapToOtherObjects(newWall.id, WallConnectionStart, start.clone(), true, { allowDuringDeferred: false });
          floorplannerStore.snapToOtherObjects(previousWallId.current, WallConnectionEnd, start.clone(), true, { allowDuringDeferred: false });
        } else {
          // Manual edge connection to ensure immediate availability for drag state
          const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
          if (prevWall) {
            // 1. Connection New -> Prev
            const conn1 = createEdgeConnection(newWall, "start", prevWall, "end", undefined, connectionSide);
            floorplannerStore.addEdgeConnection(newWall.id, prevWall.id, "start", "end", conn1);
            initialEdgeConnection = conn1;

            // 2. Connection Prev -> New (Reverse)
            const conn2 = createEdgeConnection(prevWall, "end", newWall, "start");
            floorplannerStore.addEdgeConnection(prevWall.id, newWall.id, "end", "start", conn2);

            // Ensure store knows about update
            floorplannerStore.objectNeedsUpdate(prevWall.id);
            floorplannerStore.objectNeedsUpdate(newWall.id);
          }
        }
      } else if (pendingSeedAttach) {
        // Attach the new wall's start to the hovered source wall (T-junction or endpoint).
        const sourceWall = floorplannerStore.wallsMap.get(pendingSeedAttach.wallId);
        if (sourceWall) {
          if (editorStore.isAltPressed) {
            floorplannerStore.snapToOtherObjects(newWall.id, WallConnectionStart, start.clone(), true, { allowDuringDeferred: false });
          } else {
            const conn1 = createEdgeConnection(
              newWall,
              "start",
              sourceWall,
              pendingSeedAttach.endpoint,
              pendingSeedAttach.projectedPoint,
            );
            floorplannerStore.addEdgeConnection(
              newWall.id,
              sourceWall.id,
              "start",
              pendingSeedAttach.endpoint,
              conn1,
            );
            initialEdgeConnection = conn1;
            floorplannerStore.objectNeedsUpdate(sourceWall.id);
            floorplannerStore.objectNeedsUpdate(newWall.id);
          }
        }
      }

      // Initialize drag state for pivot logic with progressive mode behavior
      // This mimics beginWallDrag: save connections, build pivot anchors, then clear connections
      if (!editorStore.isAltPressed) {
        const createdWall = floorplannerStore.wallsMap.get(newWall.id);
        if (createdWall) {
          // Store edge connections before clearing them
          const originalEdgeConnections = initialEdgeConnection
            ? [initialEdgeConnection]
            : (createdWall.edgeConnections ? [...createdWall.edgeConnections] : []);

          // Build pivot anchors for corner switching (same logic as beginWallDrag)
          const pivotAnchors: Record<"start" | "end", any> = {} as any;

          if (initialEdgeConnection && previousWallId.current && connectionSide) {
            const prevWall = floorplannerStore.wallsMap.get(previousWallId.current);
            if (prevWall) {
              const targetAngle = getWallAngle(prevWall);

              // Calculate the FIXED corners of the previous wall's end
              const prevDir = new THREE.Vector2(Math.cos(targetAngle), Math.sin(targetAngle));
              const prevPerp = new THREE.Vector2(-prevDir.y, prevDir.x);
              const prevHalfWidth = (prevWall.wallWidth || floorplannerStore.wallWidth) / 2;
              const prevCornerLeft = prevWall.end.clone().add(prevPerp.clone().multiplyScalar(prevHalfWidth));
              const prevCornerRight = prevWall.end.clone().sub(prevPerp.clone().multiplyScalar(prevHalfWidth));

              // Use connectionSide directly - we already calculated this during the iteration
              // When connectionSide is "left": new wall's LEFT corner touches prev wall's LEFT corner
              // When connectionSide is "right": new wall's RIGHT corner touches prev wall's RIGHT corner
              const primaryIsLeft = connectionSide === "left";
              const primaryAnchor = primaryIsLeft ? prevCornerLeft : prevCornerRight;
              const alternateAnchor = primaryIsLeft ? prevCornerRight : prevCornerLeft;

              // initialSideSign matches connectionSide: "left" = 1, "right" = -1
              const initialSideSign: 1 | -1 = connectionSide === "left" ? 1 : -1;

              pivotAnchors["start"] = {
                primaryAnchor,
                alternateAnchor,
                currentAnchor: "primary",
                initialSideSign,
                currentSideSign: initialSideSign,
                targetAngle,
                lastRelativeSign: initialSideSign,
                primaryIsLeft,
              };
            }
          }

          // Set drag state with saved connections and pivot anchors
          floorplannerStore.wallDragState.set(newWall.id, {
            isDragging: true,
            originalConnections: [],
            originalEdgeConnections,
            fixedEndpoints: {
              start: createdWall.start.clone(),
              end: createdWall.end.clone(),
            },
            pivotAnchors,
          });

          // CRITICAL: Clear edge connections AFTER saving them to enter progressive mode
          // This disconnects the wall visually so it renders as a simple rectangle
          if (originalEdgeConnections.length > 0) {
            floorplannerStore.clearEdgeConnections(newWall.id);

            // Clear corner state for clean progressive rendering
            floorplannerStore.setObjectProperty(newWall.id, "cornerState", undefined);
            const connectedWallId =
              previousWallId.current ?? originalEdgeConnections[0]?.targetWallId;
            if (connectedWallId) {
              floorplannerStore.setObjectProperty(connectedWallId, "cornerState", undefined);
              renderStore.clearWallShapes(connectedWallId);
              renderStore.composeWallShape(connectedWallId, undefined, true);

              // Chain-from-end draws both walls in progressive mode; hover-"+"
              // mid attachments only affect the new wall's start endcap.
              if (previousWallId.current) {
                wallUpdateManager.enableProgressiveModeForWall(connectedWallId, true);
              }
            }
          }
        }
      }
      if (!editorStore.isShiftPressed) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(newWall);
      invalidate();

      currentWall.current = newWall.id;
      addNewWall.current = false;
      wallUpdateManager.enableProgressiveModeForWall(newWall.id, true);
    }
  };

  // When the user press Enter key (like inside Edit field), editorStore.wallConstructionModeAddNewWall will signal to add a new wall
  useEffect(() => {
    if (currentWall.current && wallConstructionModeAddNewWall) {
      editorStore.setWallConstructionModeAddNewWall(false);
      const wall = floorplannerStore.wallsMap.get(currentWall.current);
      if (wall) {
        // Restore edge connections from drag state before finalizing
        const dragState = floorplannerStore.wallDragState.get(wall.id);
        if (dragState?.originalEdgeConnections && dragState.originalEdgeConnections.length > 0) {
          for (const originalEdgeConnection of dragState.originalEdgeConnections) {
            const targetWall = floorplannerStore.wallsMap.get(originalEdgeConnection.targetWallId);
            if (targetWall) {
              const newAngle = getWallAngle(wall);
              const targetAngle = getWallAngle(targetWall);
              const targetDir = new THREE.Vector2(Math.cos(targetAngle), Math.sin(targetAngle));
              const side = determineConnectionSide(targetDir, newAngle);

              const projectedPoint =
                originalEdgeConnection.targetEndpoint === "middle"
                  ? getClosestPosition(wall.start, targetWall).projectedPoint
                  : undefined;
              const conn1 = createEdgeConnection(
                wall,
                originalEdgeConnection.sourceEndpoint,
                targetWall,
                originalEdgeConnection.targetEndpoint,
                projectedPoint,
                side,
              );
              floorplannerStore.addEdgeConnection(
                wall.id,
                targetWall.id,
                originalEdgeConnection.sourceEndpoint,
                originalEdgeConnection.targetEndpoint,
                conn1,
              );

              if (
                originalEdgeConnection.targetEndpoint === "start" ||
                originalEdgeConnection.targetEndpoint === "end"
              ) {
                const conn2 = createEdgeConnection(
                  targetWall,
                  originalEdgeConnection.targetEndpoint,
                  wall,
                  originalEdgeConnection.sourceEndpoint,
                );
                floorplannerStore.addEdgeConnection(
                  targetWall.id,
                  wall.id,
                  originalEdgeConnection.targetEndpoint,
                  originalEdgeConnection.sourceEndpoint,
                  conn2,
                );
              }

              floorplannerStore.objectNeedsUpdate(targetWall.id);
            }
          }
        }

        // Disable progressive mode for the old previous wall
        if (previousWallId.current && previousWallId.current !== wall.id) {
          wallUpdateManager.enableProgressiveModeForWall(previousWallId.current, false);
          floorplannerStore.wallDragState.delete(previousWallId.current);
        }

        floorplannerStore.wallDragState.delete(wall.id);
        wallUpdateManager.enableProgressiveModeForWall(wall.id, false);
        lastClickPosition.current = wall.end.clone();
        previousWallId.current = wall.id;
        currentWall.current = undefined;
        addNewWall.current = true;
        if (!firstClickPosition.current) {
          firstClickPosition.current = wall.start.clone();
        }
      }
    }
  }, [wallConstructionModeAddNewWall]);

  useEffect(() => {
    if (!isActive) {
      const hadConstructedWall = Boolean(
        currentWall.current || previousWallId.current || firstWallId.current,
      );
      // Collect wall IDs that need finalization before clearing refs
      const wallsToFinalize: string[] = [];

      if (currentWall.current && !addNewWall.current) {
        // Force: a lease hand-off can disarm the tool mid-draw, and the
        // unfinished wall must still be discarded rather than left on canvas.
        floorplannerStore.removeWall(currentWall.current, { force: true });
      } else if (currentWall.current && addNewWall.current) {
        // Current wall was finalized, needs update
        wallsToFinalize.push(currentWall.current);
      }
      if (currentWall.current) {
        floorplannerStore.unlockMeasurementDisplaySide(currentWall.current);
      }
      if (previousWallId.current) {
        floorplannerStore.unlockMeasurementDisplaySide(previousWallId.current);
        wallsToFinalize.push(previousWallId.current);
      }
      if (firstWallId.current && !wallsToFinalize.includes(firstWallId.current)) {
        wallsToFinalize.push(firstWallId.current);
      }

      floorplannerStore.setDeferRoomUpdates(false);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      if (currentWall.current) {
        wallUpdateManager.enableProgressiveModeForWall(currentWall.current, false);
        floorplannerStore.wallDragState.delete(currentWall.current);
      }
      if (previousWallId.current) {
        wallUpdateManager.enableProgressiveModeForWall(previousWallId.current, false);
        floorplannerStore.wallDragState.delete(previousWallId.current);
      }
      renderStore.setIsDragging(false);
      renderStore.clearAlignmentLines();
      editorStore.setWallEditingOuterLength(undefined);
      editorStore.setWallEditingInnerLength(undefined);
      editorStore.setWallEditingLengthPanel(undefined);

      // Finalize all walls after exiting construction mode
      // This ensures joint polygons, outlines, and corner fills are properly generated
      wallsToFinalize.forEach(wallId => {
        const wall = floorplannerStore.wallsMap.get(wallId);
        if (wall) {
          // Force a full geometry update
          floorplannerStore.objectNeedsUpdate(wallId);

          // Regenerate joint polygons for proper corner fills
          renderStore.generateJointPolygons(wallId);

          // Also update connected walls to ensure their joints are correct
          if (wall.edgeConnections) {
            wall.edgeConnections.forEach(conn => {
              const connectedWall = floorplannerStore.wallsMap.get(conn.targetWallId);
              if (connectedWall) {
                floorplannerStore.objectNeedsUpdate(conn.targetWallId);
                renderStore.generateJointPolygons(conn.targetWallId);
              }
            });
          }
        }
      });

      addNewWall.current = false;
      currentWall.current = undefined;
      lastClickPosition.current = undefined;
      firstClickPosition.current = undefined;
      previousWallId.current = undefined;
      firstWallId.current = undefined;
      seedAttach.current = null;
      lastAppliedSeedRequestId.current = 0;
      editorStore.clearWallConstructionSeedStart();
      document.body.style.cursor = 'auto';
      floorplannerStore.setBlockDirty(false);
      if (hadConstructedWall) {
        floorplannerStore.setDirty();
      }
      return;
    }

    // Seed from hover "+" so the first click is already placed on the source wall.
    // Keep the store seed until mode exit so React Strict Mode remounts can re-apply
    // after refs are reset.
    const seed = editorStore.wallConstructionSeedStart;
    if (seed && wallConstructionSeedRequestId !== lastAppliedSeedRequestId.current) {
      lastAppliedSeedRequestId.current = wallConstructionSeedRequestId;
      const start = new THREE.Vector2(seed.x, seed.y);
      const projected = new THREE.Vector2(seed.projectedX, seed.projectedY);
      lastClickPosition.current = start.clone();
      firstClickPosition.current = start.clone();
      addNewWall.current = true;
      currentWall.current = undefined;

      if (seed.attachEndpoint === "end") {
        // Continue from the source wall's end using the normal chain path.
        previousWallId.current = seed.sourceWallId;
        seedAttach.current = null;
        const sourceWall = floorplannerStore.wallsMap.get(seed.sourceWallId);
        if (sourceWall) {
          lastClickPosition.current = sourceWall.end.clone();
          firstClickPosition.current = sourceWall.end.clone();
        }
      } else {
        previousWallId.current = undefined;
        seedAttach.current = {
          wallId: seed.sourceWallId,
          endpoint: seed.attachEndpoint,
          projectedPoint: projected,
        };
      }

      editorStore.clearSelections();

      // Immediately place a minimum-length stub so the wall is obvious from the first frame.
      const outward = new THREE.Vector2(seed.outwardX, seed.outwardY);
      if (outward.lengthSq() > 1e-12) {
        outward.normalize();
      } else {
        outward.set(0, 1);
      }
      const minLength = Math.max(
        WALL_ADD_MIN_INITIAL_LENGTH,
        (floorplannerStore.wallWidth || 0.1) * 2,
      );
      const initialEnd = lastClickPosition.current!.clone().add(outward.multiplyScalar(minLength));
      createNewWallSegment([initialEnd.x, initialEnd.y]);
      // Seed has been applied into an actual wall; drop it so remounts cannot spawn a duplicate.
      editorStore.clearWallConstructionSeedStart();
    }

    floorplannerStore.setDeferRoomUpdates(true);
    renderStore.setIsDragging(true);
    document.body.classList.remove("cursor-vector");
    document.body.style.cursor = 'crosshair';
    floorplannerStore.setBlockDirty(true);
  }, [isActive, wallConstructionMode, wallConstructionSeedRequestId, view3D]);

  useEffect(() => {
    if (wallConstructionModeUndoLastWallRequest === lastHandledUndoRequest.current) {
      return;
    }

    lastHandledUndoRequest.current = wallConstructionModeUndoLastWallRequest;

    if (isActive) {
      undoLatestConstructionWall();
    }
  }, [isActive, wallConstructionModeUndoLastWallRequest]);

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      if (currentWall.current) {
        wallUpdateManager.enableProgressiveModeForWall(currentWall.current, false);
        floorplannerStore.unlockMeasurementDisplaySide(currentWall.current);
      }
      if (previousWallId.current) {
        wallUpdateManager.enableProgressiveModeForWall(previousWallId.current, false);
        floorplannerStore.unlockMeasurementDisplaySide(previousWallId.current);
      }
      floorplannerStore.setDeferRoomUpdates(false);
      renderStore.setIsDragging(false);
    };
  }, []);

  if (!isActive) {
    return null;
  }

  return (
    <mesh
      position={[0, 0, -0.01]} // Ensure the plane is in the background
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      //onDoubleClick={handleDoubleClick}
      userData={{ id: 'wall-construction-plane' }}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
});

export default WallConstructionPlane;
